import { FormEvent, FC, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import sharedClasses from '@modals/index.module.css'
import { IcoRemove } from '@/utils/icons'
import { crmLeadContractApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import TemplateContractForm from '@pages/Admin/AllUsers/modals/AddContractModal/AddTemplateContractModal/components/TemplateContractForm'
import { useContractTemplates } from '@pages/Admin/AllUsers/modals/AddContractModal/AddTemplateContractModal/hooks/useContractTemplates'
import { useTemplateConstructor } from '@pages/Admin/AllUsers/modals/AddContractModal/AddTemplateContractModal/hooks/useTemplateConstructor'
import {
  getEmptyRequiredFieldCodes,
  parseProductOptionKey,
} from '@pages/Admin/AllUsers/modals/AddContractModal/AddTemplateContractModal/utils/contractTemplateConstructor.utils'
import { getErrorText } from '@pages/Admin/AllUsers/modals/AddContractModal/AddTemplateContractModal/utils/getErrorText'
import classes from '@pages/Admin/AllUsers/modals/AddContractModal/AddTemplateContractModal/AddTemplateContractModal.module.css'

type LeadAddTemplateContractModalProps = {
  open: boolean
  leadId: number
  /** Привязанный ученик — для автозаполнения конструктора. */
  linkedUserId?: number | null
  onClose: () => void
  onSuccess: () => void | Promise<void>
}

const LeadAddTemplateContractModal: FC<LeadAddTemplateContractModalProps> = ({
  open,
  leadId,
  linkedUserId,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { templatesLoading, templateOptions } = useContractTemplates(open)

  const {
    templateId,
    constructorData,
    constructorGroups,
    constructorLoading,
    parentLoading,
    productLoading,
    documentName,
    setDocumentName,
    values,
    selectedParentId,
    selectedProductKey,
    invalidFieldCodes,
    setInvalidFieldCodes,
    recalculatingCodes,
    parentOptions,
    productOptions,
    shouldShowProductSelector,
    handleTemplateChange,
    handleParentChange,
    handleProductChange,
    handleFieldChange,
  } = useTemplateConstructor({
    active: open,
    userId: linkedUserId,
    leadId,
  })

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (templateId == null || !leadId || !constructorData) {
      return
    }

    const emptyFields = getEmptyRequiredFieldCodes(constructorGroups, values)

    if (!documentName.trim()) {
      addAlert({
        title: 'Ошибка',
        text: 'Укажите название договора',
        type: 'error',
      })
      return
    }

    if (emptyFields.length) {
      setInvalidFieldCodes(new Set(emptyFields))
      addAlert({
        title: 'Ошибка',
        text: 'Заполните все поля, используемые в шаблоне договора',
        type: 'error',
      })
      return
    }

    const { product_type, product_id } = parseProductOptionKey(
      selectedProductKey,
    )

    setIsSubmitting(true)
    try {
      const res = await crmLeadContractApi.createFromTemplate({
        lead_id: leadId,
        template_id: Number(templateId),
        document_name: documentName.trim(),
        ...(selectedParentId != null ? { parent_id: selectedParentId } : {}),
        product_type,
        ...(product_type !== 'custom' && product_id != null
          ? { product_id }
          : {}),
        values,
      })
      if (isError(res)) throw new Error()

      await onSuccess()
      addAlert({ title: 'Договор успешно создан' })
      onClose()
    } catch (error) {
      addAlert({
        title: 'Ошибка',
        text: getErrorText(error, 'Не удалось создать договор из шаблона'),
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!open) return null

  return createPortal(
    <Modal
      active={open}
      setActive={onClose}
      className={classes.modalWrapper as any}
      style={{ zIndex: '110' }}
      styleContent={{
        minWidth: 'auto',
        padding: 0,
      }}
    >
      <div
        className={classNames(
          sharedClasses.modalContainer,
          classes.modalContainer,
        )}
      >
        <div className={sharedClasses.modalHeader}>
          <div className={sharedClasses.modalTitleContainer}>
            <span className={sharedClasses.modalHeaderBigTitle}>
              Загрузка шаблонного договора
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={onClose}>
            <IcoRemove />
          </div>
        </div>

        <TemplateContractForm
          templateId={templateId}
          templateOptions={templateOptions}
          templatesLoading={templatesLoading}
          constructorLoading={constructorLoading}
          parentLoading={parentLoading}
          productLoading={productLoading}
          isSubmitting={isSubmitting}
          documentName={documentName}
          values={values}
          invalidFieldCodes={invalidFieldCodes}
          recalculatingCodes={recalculatingCodes}
          constructorGroups={constructorGroups}
          parentOptions={parentOptions}
          productOptions={productOptions}
          shouldShowProductSelector={shouldShowProductSelector}
          selectedParentId={selectedParentId}
          selectedProductKey={selectedProductKey}
          hasConstructorData={Boolean(constructorData)}
          onTemplateChange={handleTemplateChange}
          onParentChange={handleParentChange}
          onProductChange={handleProductChange}
          onDocumentNameChange={setDocumentName}
          onFieldChange={handleFieldChange}
          onSubmit={(e) => void handleSubmit(e)}
        />
      </div>
    </Modal>,
    document.body,
  )
}

export default LeadAddTemplateContractModal
